import { useAtom } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { VisitorDrawerState, visitorDrawerStateAtom } from '../state';
import { useGetShouldShowReflectionTopics } from './useGetShouldShowReflectionTopics';

/**
 * Utility hook encapsulating shared logic for reflection topics drawer controls.
 *
 * @returns An object containing the necessary props for the reflection topics drawer control.
 * @property helperText - The text to display in the tooltip or menu item.
 * @property isDrawerOpen - Whether the reflection topics drawer is already open.
 * @property onClick - The function to call when the drawer control is clicked.
 * @property shouldShowButton - Whether the button should be shown.
 */
export function useReflectionTopicsDrawerControl() {
  const t = useTranslate();
  const [drawerState, setDrawerState] = useAtom(visitorDrawerStateAtom);
  const { shouldShowReflectionTopics } = useGetShouldShowReflectionTopics();
  const isDrawerOpen = drawerState === VisitorDrawerState.REFLECTION_TOPICS;
  const helperText = isDrawerOpen
    ? t('telehealth-core:controls.closeSessionTopics')
    : t('telehealth-core:controls.openSessionTopics');

  const onClick = useCallback(() => {
    setDrawerState((prev) =>
      prev === VisitorDrawerState.REFLECTION_TOPICS ? null : VisitorDrawerState.REFLECTION_TOPICS,
    );
  }, [setDrawerState]);

  return {
    helperText,
    isDrawerOpen,
    onClick,
    shouldShowButton: shouldShowReflectionTopics,
  };
}
